'use client';

import FuseLoading from '@fuse/core/FuseLoading';
import FusePageCarded from '@fuse/core/FusePageCarded';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Link from '@fuse/core/Link';
import { FormProvider, useForm } from 'react-hook-form';
import useThemeMediaQuery from '@fuse/hooks/useThemeMediaQuery';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import UserHeader from './UserHeader';
import BasicInfoTab from './BasicInfoTab';
import { useGetUserQuery } from '@/store/services/users';
import UserModel from '../../models/UserModel';

/**
 * Form Validation Schema
 */
const schema = z.object({
	name: z.string().nonempty('You must enter a user name').min(5, 'The user name must be at least 5 characters'),
	email: z.string().email('Invalid email').nonempty('Email is required')
});

/**
 * The product page.
 */
function Product() {
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

	const routeParams = useParams<{ userId: string }>();

	const { userId } = routeParams;

	const {
		data: user,
		isLoading,
		isError
	} = useGetUserQuery(userId, {
		skip: !userId || userId === 'new'
	});

	const methods = useForm({
		mode: 'onChange',
		defaultValues: {},
		resolver: zodResolver(schema)
	});

	const { reset, watch } = methods;

	const form = watch();

	useEffect(() => {
		if (userId === 'new') {
			reset(UserModel({}));
		}
	}, [userId, reset]);

	useEffect(() => {
		if (user) {
			reset({ name: user?.data?.name, email: user?.data?.email, role_id: user?.data?.role_id });
		}
	}, [user, reset]);

	if (isLoading) {
		return <FuseLoading />;
	}

	/**
	 * Show Message if the requested products is not exists
	 */
	if (isError && userId !== 'new') {
		return (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1, transition: { delay: 0.1 } }}
				className="flex flex-col flex-1 items-center justify-center h-full"
			>
				<Typography
					color="text.secondary"
					variant="h5"
				>
					There is no such user!
				</Typography>
				<Button
					className="mt-6"
					component={Link}
					variant="outlined"
					to="/apps/users"
					color="inherit"
				>
					Go to Users Page
				</Button>
			</motion.div>
		);
	}

	return (
		<FormProvider {...methods}>
			<FusePageCarded
				header={<UserHeader />}
				content={
					<div className="p-4 sm:p-6 max-w-5xl space-y-6">
						<BasicInfoTab />
					</div>
				}
				scroll={isMobile ? 'normal' : 'content'}
			/>
		</FormProvider>
	);
}

export default Product;
